import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAllPlans,
  createPlan,
  updatePlan,
  deletePlan,
  fetchCapabilityCatalogue,
  type CapabilityEntry,
  type SubscriptionPlan,
  type SubscriptionPlanCreate,
} from '../api/admin';

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtPrice(cents: number): string {
  if (cents === 0) return 'Free';
  return `$${(cents / 100).toFixed(0)}`;
}

/* ------------------------------------------------------------------ */
/*  Plan Form Modal                                                    */
/* ------------------------------------------------------------------ */

const EMPTY_FORM: SubscriptionPlanCreate = {
  name: '',
  slug: '',
  description: '',
  price_cents: 0,
  billing_interval: 'monthly',
  monthly_event_limit: 50_000,
  monthly_ai_token_limit: 500_000,
  monthly_session_limit: 2_000,
  max_sites: 1,
  max_seats: 2,
  max_voice_clones: 1,
  data_retention_days: 7,
  event_overage_policy: 'hard_block',
  event_overage_price_cents: 0,
  features: [],
  trial_days: 0,
  is_active: true,
  display_order: 0,
  // Credit system
  monthly_credits: 0,
  credit_rate_multiplier: 1,
  credit_overage_policy: 'hard_block',
  byok_allowed: true,
  // Left undefined so the API seeds the shipped defaults for the slug rather
  // than creating a plan with every gate explicitly blank.
  entitlements: undefined,
  quotas: undefined,
  contact_sales: false,
  min_topup_cents: 0,
  credit_price_cents_per_1000: 0,
};

const UNLIMITED = -1;

/**
 * Toggles for every gate the API can check, grouped by the tier that unlocks
 * each one by default, so an admin sees what they are departing from.
 *
 * A gate the admin has not touched has no key in `entitlements`; the API then
 * falls back to that shipped default. Setting one writes an explicit override.
 */
function EntitlementEditor({
  catalogue,
  slug,
  value,
  onChange,
}: {
  catalogue: CapabilityEntry[];
  slug: string;
  value: Record<string, boolean> | undefined;
  onChange: (next: Record<string, boolean>) => void;
}) {
  const planRank = (p?: string) =>
    ({ free: 0, starter: 0, pro: 1, business: 2, enterprise: 3, payg: 3 })[p ?? ''] ?? 0;
  const shippedDefault = (entry: CapabilityEntry) =>
    // A plan the ladder cannot describe (payg unlocks the higher tiers but is
    // denied live voice and voice cloning) states its default explicitly.
    entry.plan_overrides?.[slug] ?? planRank(slug) >= planRank(entry.default_min_plan);

  return (
    <div className="space-y-2">
      {catalogue.map((entry) => {
        const override = value?.[entry.key];
        const effective = override ?? shippedDefault(entry);
        const isOverride = override !== undefined && override !== shippedDefault(entry);
        return (
          <label
            key={entry.key}
            className="flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-2"
          >
            <input
              type="checkbox"
              checked={effective}
              onChange={(e) => onChange({ ...(value ?? {}), [entry.key]: e.target.checked })}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-slate-800">{entry.label}</span>
                {isOverride && (
                  <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                    overridden
                  </span>
                )}
              </span>
              {entry.description && (
                <span className="mt-0.5 block text-xs text-slate-500">{entry.description}</span>
              )}
            </span>
          </label>
        );
      })}
    </div>
  );
}

/** Per-period allowances. Blank means "use the shipped default"; -1 is unlimited. */
function QuotaEditor({
  catalogue,
  value,
  onChange,
}: {
  catalogue: CapabilityEntry[];
  value: Record<string, number> | undefined;
  onChange: (next: Record<string, number>) => void;
}) {
  return (
    <div className="space-y-2">
      {catalogue.map((entry) => {
        const current = value?.[entry.key];
        return (
          <div key={entry.key} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-slate-800">{entry.label}</span>
              {entry.description && (
                <span className="mt-0.5 block text-xs text-slate-500">{entry.description}</span>
              )}
            </span>
            <input
              type="number"
              min={UNLIMITED}
              value={current ?? ''}
              placeholder="default"
              aria-label={`${entry.label} per period`}
              onChange={(e) => {
                const raw = e.target.value;
                const next = { ...(value ?? {}) };
                if (raw === '') delete next[entry.key];
                else next[entry.key] = parseInt(raw, 10);
                onChange(next);
              }}
              className="w-24 shrink-0 px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-purple-500/20"
            />
            <button
              type="button"
              onClick={() => onChange({ ...(value ?? {}), [entry.key]: UNLIMITED })}
              className="shrink-0 rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              ∞
            </button>
          </div>
        );
      })}
    </div>
  );
}

function PlanFormModal({
  plan,
  onClose,
  onSave,
  saving,
}: {
  plan: SubscriptionPlanCreate & { id?: string };
  onClose: () => void;
  onSave: (data: SubscriptionPlanCreate) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<SubscriptionPlanCreate>({ ...plan });
  const [featuresText, setFeaturesText] = useState((plan.features || []).join('\n'));
  // The set of gates comes from the API so a gate added on the server appears
  // here without a matching front-end change.
  const { data: catalogue } = useQuery({
    queryKey: ['capability-catalogue'],
    queryFn: fetchCapabilityCatalogue,
    staleTime: Infinity,
  });

  const set = <K extends keyof SubscriptionPlanCreate>(key: K, value: SubscriptionPlanCreate[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...form,
      features: featuresText.split('\n').map((s) => s.trim()).filter(Boolean),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">
            {plan.id ? 'Edit Plan' : 'Create Plan'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name & Slug */}
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</span>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="e.g. Pro"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Slug</span>
              <input
                type="text"
                required
                pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
                value={form.slug}
                onChange={(e) => set('slug', e.target.value.toLowerCase())}
                placeholder="e.g. pro"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              />
            </label>
          </div>

          {/* Description */}
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Description</span>
            <textarea
              value={form.description || ''}
              onChange={(e) => set('description', e.target.value)}
              rows={2}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
            />
          </label>

          {/* Price & Billing */}
          <div className="grid grid-cols-3 gap-4">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Price (cents)</span>
              <input
                type="number"
                min={0}
                value={form.price_cents}
                onChange={(e) => set('price_cents', parseInt(e.target.value) || 0)}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              />
              <span className="text-[10px] text-slate-400">{fmtPrice(form.price_cents || 0)}/mo</span>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Billing</span>
              <select
                value={form.billing_interval}
                onChange={(e) => set('billing_interval', e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Trial days</span>
              <input
                type="number"
                min={0}
                value={form.trial_days}
                onChange={(e) => set('trial_days', parseInt(e.target.value) || 0)}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              />
            </label>
          </div>

          {/* Limits */}
          <div>
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Limits</span>
            <div className="mt-2 grid grid-cols-3 gap-3">
              <label className="block">
                <span className="text-[10px] text-slate-400 font-semibold">Events/mo</span>
                <input
                  type="number"
                  min={0}
                  value={form.monthly_event_limit}
                  onChange={(e) => set('monthly_event_limit', parseInt(e.target.value) || 0)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
              </label>
              <label className="block">
                <span className="text-[10px] text-slate-400 font-semibold">AI Tokens/mo</span>
                <input
                  type="number"
                  min={0}
                  value={form.monthly_ai_token_limit}
                  onChange={(e) => set('monthly_ai_token_limit', parseInt(e.target.value) || 0)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
              </label>
              <label className="block">
                <span className="text-[10px] text-slate-400 font-semibold">Sessions/mo</span>
                <input
                  type="number"
                  min={0}
                  value={form.monthly_session_limit}
                  onChange={(e) => set('monthly_session_limit', parseInt(e.target.value) || 0)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
              </label>
              <label className="block">
                <span className="text-[10px] text-slate-400 font-semibold">Max Sites (-1 = unlimited)</span>
                <input
                  type="number"
                  min={-1}
                  value={form.max_sites}
                  onChange={(e) => set('max_sites', parseInt(e.target.value) || 0)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
              </label>
              <label className="block">
                <span className="text-[10px] text-slate-400 font-semibold">Max Seats</span>
                <input
                  type="number"
                  min={-1}
                  value={form.max_seats}
                  onChange={(e) => set('max_seats', parseInt(e.target.value) || 0)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
              </label>
              <label className="block">
                <span className="text-[10px] text-slate-400 font-semibold">Max Voice Clones (-1 = unlimited)</span>
                <input
                  type="number"
                  min={-1}
                  value={form.max_voice_clones}
                  onChange={(e) => set('max_voice_clones', parseInt(e.target.value) || 0)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
              </label>
              <label className="block">
                <span className="text-[10px] text-slate-400 font-semibold">Data Retention (days)</span>
                <input
                  type="number"
                  min={1}
                  value={form.data_retention_days}
                  onChange={(e) => set('data_retention_days', parseInt(e.target.value) || 7)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
              </label>
            </div>
          </div>

          {/* Overage */}
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Event Overage Policy</span>
              <select
                value={form.event_overage_policy}
                onChange={(e) => set('event_overage_policy', e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              >
                <option value="hard_block">Hard Block</option>
                <option value="overage">Allow Overage</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Overage Price (cents/50k)</span>
              <input
                type="number"
                min={0}
                value={form.event_overage_price_cents}
                onChange={(e) => set('event_overage_price_cents', parseInt(e.target.value) || 0)}
                disabled={form.event_overage_policy === 'hard_block'}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 disabled:opacity-50"
              />
            </label>
          </div>

          {/* Credit System */}
          <div>
            <span className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Credit System</span>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <label className="block">
                <span className="text-[10px] text-slate-400 font-semibold">Monthly Credits</span>
                <input
                  type="number"
                  min={0}
                  value={form.monthly_credits ?? 0}
                  onChange={(e) => set('monthly_credits', parseInt(e.target.value) || 0)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
              </label>
              <label className="block">
                <span className="text-[10px] text-slate-500 font-semibold">Usage Rate Multiplier</span>
                <input
                  type="number"
                  min={0.01}
                  max={100}
                  step={0.01}
                  required
                  value={form.credit_rate_multiplier ?? 1}
                  onChange={(e) => set('credit_rate_multiplier', Number(e.target.value) || 1)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
                <span className="text-[10px] text-slate-400">1× standard · 2× double usage</span>
              </label>
              <label className="block">
                <span className="text-[10px] text-slate-400 font-semibold">Credit Overage Policy</span>
                <select
                  value={form.credit_overage_policy ?? 'hard_block'}
                  onChange={(e) => set('credit_overage_policy', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                >
                  <option value="hard_block">Hard Block</option>
                  <option value="overage">Allow Overage</option>
                </select>
              </label>
              <label className="flex items-center gap-3 mt-6">
                <input
                  type="checkbox"
                  checked={form.byok_allowed ?? true}
                  onChange={(e) => set('byok_allowed', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-slate-700">BYOK Allowed</span>
              </label>
            </div>
          </div>

          {/* Features */}
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Features (one per line)</span>
            <textarea
              value={featuresText}
              onChange={(e) => setFeaturesText(e.target.value)}
              rows={4}
              placeholder="All features&#10;Priority support&#10;Custom branding"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-500/20"
            />
          </label>

          {/* Gates — what this plan may do, and how much of it */}
          <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-4 space-y-4">
            <div>
              <h3 className="text-xs font-semibold text-purple-900 uppercase tracking-wider">
                Feature access
              </h3>
              <p className="mt-1 text-xs text-slate-600">
                What this plan may use at all. Unticking a box blocks the feature with a
                402 and an upgrade prompt.
              </p>
              <div className="mt-3">
                {catalogue ? (
                  <EntitlementEditor
                    catalogue={catalogue.features}
                    slug={form.slug}
                    value={form.entitlements}
                    onChange={(next) => set('entitlements', next)}
                  />
                ) : (
                  <p className="text-xs text-slate-500">Loading gates…</p>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-purple-900 uppercase tracking-wider">
                Per-period allowances
              </h3>
              <p className="mt-1 text-xs text-slate-600">
                How many of each per billing period, resetting with the credit period.
                Blank uses the shipped default; −1 (∞) is unlimited.
              </p>
              <div className="mt-3">
                {catalogue ? (
                  <QuotaEditor
                    catalogue={catalogue.quotas}
                    value={form.quotas}
                    onChange={(next) => set('quotas', next)}
                  />
                ) : (
                  <p className="text-xs text-slate-500">Loading allowances…</p>
                )}
              </div>
            </div>
          </div>

          {/* Sales-gated plans and credit pricing (Pay as you go) */}
          <div className="rounded-xl border border-slate-200 p-4 space-y-4">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.contact_sales ?? false}
                onChange={(e) => set('contact_sales', e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm font-medium text-slate-700">
                Contact sales — show “Book a demo” instead of a price
              </span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Minimum purchase (USD cents)
                </span>
                <input
                  type="number"
                  min={0}
                  value={form.min_topup_cents ?? 0}
                  onChange={(e) => set('min_topup_cents', parseInt(e.target.value) || 0)}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
                <span className="mt-1 block text-xs text-slate-500">
                  {form.min_topup_cents
                    ? `Smallest credit purchase: $${((form.min_topup_cents ?? 0) / 100).toFixed(2)}`
                    : 'No minimum.'}
                </span>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Price per 1,000 credits (USD cents)
                </span>
                <input
                  type="number"
                  min={0}
                  value={form.credit_price_cents_per_1000 ?? 0}
                  onChange={(e) =>
                    set('credit_price_cents_per_1000', parseInt(e.target.value) || 0)
                  }
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
                <span className="mt-1 block text-xs text-slate-500">
                  {form.credit_price_cents_per_1000
                    ? `$${((form.min_topup_cents ?? 0) / 100).toFixed(2)} buys ~${Math.round(
                        ((form.min_topup_cents ?? 0) * 1000) / form.credit_price_cents_per_1000,
                      ).toLocaleString()} credits`
                    : 'Credits are not sold directly on this plan.'}
                </span>
              </label>
            </div>
          </div>

          {/* Display order & Active */}
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Display Order</span>
              <input
                type="number"
                min={0}
                value={form.display_order}
                onChange={(e) => set('display_order', parseInt(e.target.value) || 0)}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              />
            </label>
            <label className="flex items-center gap-3 mt-6">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => set('is_active', e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm font-medium text-slate-700">Active</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : plan.id ? 'Save Changes' : 'Create Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Plan Card                                                          */
/* ------------------------------------------------------------------ */

function PlanCard({
  plan,
  onEdit,
  onToggle,
  toggling,
}: {
  plan: SubscriptionPlan;
  onEdit: () => void;
  onToggle: () => void;
  toggling: boolean;
}) {
  return (
    <div className={`bg-white rounded-xl border ${plan.is_active ? 'border-slate-200' : 'border-red-200 opacity-60'} overflow-hidden`}>
      <div className="px-6 py-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{plan.name}</h3>
            <div className="text-xs text-slate-400 font-mono">{plan.slug}</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-slate-900">{fmtPrice(plan.price_cents)}</div>
            {plan.price_cents > 0 && <div className="text-xs text-slate-400">/{plan.billing_interval === 'yearly' ? 'yr' : 'mo'}</div>}
          </div>
        </div>

        {plan.description && (
          <p className="text-sm text-slate-500 mb-4">{plan.description}</p>
        )}

        {/* Limits grid */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-slate-50 rounded-lg p-2.5">
            <div className="text-[10px] text-slate-400 font-semibold uppercase">Events</div>
            <div className="text-sm font-bold text-slate-900">{fmtNum(plan.monthly_event_limit)}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2.5">
            <div className="text-[10px] text-slate-400 font-semibold uppercase">AI Tokens</div>
            <div className="text-sm font-bold text-slate-900">{fmtNum(plan.monthly_ai_token_limit)}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2.5">
            <div className="text-[10px] text-slate-400 font-semibold uppercase">Sessions</div>
            <div className="text-sm font-bold text-slate-900">{fmtNum(plan.monthly_session_limit)}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2.5">
            <div className="text-[10px] text-slate-400 font-semibold uppercase">Sites</div>
            <div className="text-sm font-bold text-slate-900">{plan.max_sites === -1 ? 'Unlimited' : plan.max_sites}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2.5">
            <div className="text-[10px] text-slate-400 font-semibold uppercase">Seats</div>
            <div className="text-sm font-bold text-slate-900">{plan.max_seats === -1 ? 'Unlimited' : plan.max_seats}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2.5">
            <div className="text-[10px] text-slate-400 font-semibold uppercase">Voice Clones</div>
            <div className="text-sm font-bold text-slate-900">{plan.max_voice_clones === -1 || plan.max_voice_clones === 0 ? 'Unlimited' : plan.max_voice_clones}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2.5">
            <div className="text-[10px] text-slate-400 font-semibold uppercase">Retention</div>
            <div className="text-sm font-bold text-slate-900">{plan.data_retention_days}d</div>
          </div>
        </div>

        {/* Credits row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-purple-50 rounded-lg p-2.5">
            <div className="text-[10px] text-purple-400 font-semibold uppercase">Credits/mo</div>
            <div className="text-sm font-bold text-purple-900">{fmtNum(plan.monthly_credits)}</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-2.5">
            <div className="text-[10px] text-purple-500 font-semibold uppercase">Usage Rate</div>
            <div className="text-sm font-bold text-purple-900">{Number(plan.credit_rate_multiplier ?? 1).toFixed(2).replace(/\.00$/, '')}×</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-2.5">
            <div className="text-[10px] text-purple-400 font-semibold uppercase">Cost/Credit</div>
            <div className="text-sm font-bold text-purple-900">
              {plan.monthly_credits > 0 && plan.price_cents > 0
                ? `$${(plan.price_cents / 100 / plan.monthly_credits).toFixed(4)}`
                : plan.price_cents === 0 ? 'Free' : '—'}
            </div>
          </div>
          <div className="bg-purple-50 rounded-lg p-2.5">
            <div className="text-[10px] text-purple-400 font-semibold uppercase">Credit Overage</div>
            <div className="text-sm font-bold text-purple-900">{plan.credit_overage_policy === 'hard_block' ? 'Block' : 'Allow'}</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-2.5">
            <div className="text-[10px] text-purple-400 font-semibold uppercase">BYOK</div>
            <div className="text-sm font-bold text-purple-900">{plan.byok_allowed ? 'Yes' : 'No'}</div>
          </div>
        </div>

        {/* Overage */}
        <div className="text-xs text-slate-500 mb-4">
          <span className="font-semibold">Event Overage: </span>
          {plan.event_overage_policy === 'hard_block'
            ? 'Hard block at limit'
            : `$${(plan.event_overage_price_cents / 100).toFixed(0)} per 50K extra events`}
        </div>

        {/* Features */}
        {plan.features && plan.features.length > 0 && (
          <div className="mb-4">
            <div className="text-[10px] text-slate-400 font-semibold uppercase mb-1">Features</div>
            <ul className="space-y-1">
              {plan.features.map((f, i) => (
                <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Meta */}
        <div className="flex items-center gap-3 text-[10px] text-slate-400">
          {plan.trial_days > 0 && <span>{plan.trial_days}-day trial</span>}
          <span>Order: {plan.display_order}</span>
          <span className={`px-1.5 py-0.5 rounded-full font-bold uppercase ${plan.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
            {plan.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center gap-2">
        <button
          onClick={onEdit}
          className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
        >
          Edit
        </button>
        <button
          onClick={onToggle}
          disabled={toggling}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg border ${
            plan.is_active
              ? 'text-red-600 border-red-200 hover:bg-red-50'
              : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'
          } disabled:opacity-50`}
        >
          {toggling ? '...' : plan.is_active ? 'Deactivate' : 'Activate'}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */

export function PlansPage() {
  const queryClient = useQueryClient();
  const [modalPlan, setModalPlan] = useState<(SubscriptionPlanCreate & { id?: string }) | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  const plansQuery = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: listAllPlans,
  });

  const createMutation = useMutation({
    mutationFn: (data: SubscriptionPlanCreate) => createPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
      setModalPlan(null);
      setSuccessMsg('Plan created');
      setTimeout(() => setSuccessMsg(''), 3000);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SubscriptionPlanCreate> }) => updatePlan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
      setModalPlan(null);
      setSuccessMsg('Plan updated');
      setTimeout(() => setSuccessMsg(''), 3000);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      if (is_active) {
        await deletePlan(id);
        return null;
      }
      return updatePlan(id, { is_active: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });
    },
  });

  const plans = plansQuery.data || [];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Subscription Plans</h1>
          <p className="text-slate-600 mt-2">
            Create and manage subscription plans for organizations.
          </p>
        </div>
        <button
          onClick={() => setModalPlan({ ...EMPTY_FORM })}
          className="px-4 py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Plan
        </button>
      </div>

      {successMsg && (
        <div className="mb-6 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm font-semibold text-emerald-700">
          {successMsg}
        </div>
      )}

      {plansQuery.isLoading && (
        <div className="text-center py-12 text-slate-400">Loading plans...</div>
      )}

      {!plansQuery.isLoading && plans.length === 0 && (
        <div className="text-center py-12 text-slate-400">No plans found. Create your first plan.</div>
      )}

      {!plansQuery.isLoading && plans.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onEdit={() =>
                setModalPlan({
                  id: plan.id,
                  name: plan.name,
                  slug: plan.slug,
                  description: plan.description || '',
                  price_cents: plan.price_cents,
                  billing_interval: plan.billing_interval,
                  monthly_event_limit: plan.monthly_event_limit,
                  monthly_ai_token_limit: plan.monthly_ai_token_limit,
                  monthly_session_limit: plan.monthly_session_limit,
                  max_sites: plan.max_sites,
                  max_seats: plan.max_seats,
                  max_voice_clones: plan.max_voice_clones,
                  data_retention_days: plan.data_retention_days,
                  event_overage_policy: plan.event_overage_policy,
                  event_overage_price_cents: plan.event_overage_price_cents,
                  features: plan.features || [],
                  trial_days: plan.trial_days,
                  is_active: plan.is_active,
                  display_order: plan.display_order,
                  monthly_credits: plan.monthly_credits,
                  credit_rate_multiplier: plan.credit_rate_multiplier,
                  credit_overage_policy: plan.credit_overage_policy,
                  byok_allowed: plan.byok_allowed,
                  // The gates are stored as overrides, so an unloaded map made
                  // the editor show shipped defaults for a plan the admin had
                  // already customised — and a single tick then saved a map
                  // holding only that one key, dropping the rest.
                  entitlements: plan.entitlements ?? undefined,
                  quotas: plan.quotas ?? undefined,
                  contact_sales: plan.contact_sales,
                  min_topup_cents: plan.min_topup_cents,
                  credit_price_cents_per_1000: plan.credit_price_cents_per_1000,
                })
              }
              onToggle={() => toggleMutation.mutate({ id: plan.id, is_active: plan.is_active })}
              toggling={toggleMutation.isPending}
            />
          ))}
        </div>
      )}

      {/* Create/Edit modal */}
      {modalPlan && (
        <PlanFormModal
          plan={modalPlan}
          onClose={() => setModalPlan(null)}
          saving={createMutation.isPending || updateMutation.isPending}
          onSave={(data) => {
            if (modalPlan.id) {
              updateMutation.mutate({ id: modalPlan.id, data });
            } else {
              createMutation.mutate(data);
            }
          }}
        />
      )}
    </div>
  );
}
